// src/Pages/Quote/Quote.jsx
import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import "./Quote.css";
import { useLead } from "../../Context/LeadContext";

const STATES = [
  { abbr: "", name: "Select a state" },
  { abbr: "AL", name: "Alabama" },
  { abbr: "AK", name: "Alaska" },
  { abbr: "AZ", name: "Arizona" },
  { abbr: "AR", name: "Arkansas" },
  { abbr: "CA", name: "California" },
  { abbr: "CO", name: "Colorado" },
  { abbr: "CT", name: "Connecticut" },
  { abbr: "DE", name: "Delaware" },
  { abbr: "DC", name: "District of Columbia" },
  { abbr: "FL", name: "Florida" },
  { abbr: "GA", name: "Georgia" },
  { abbr: "HI", name: "Hawaii" },
  { abbr: "ID", name: "Idaho" },
  { abbr: "IL", name: "Illinois" },
  { abbr: "IN", name: "Indiana" },
  { abbr: "IA", name: "Iowa" },
  { abbr: "KS", name: "Kansas" },
  { abbr: "KY", name: "Kentucky" },
  { abbr: "LA", name: "Louisiana" },
  { abbr: "ME", name: "Maine" },
  { abbr: "MD", name: "Maryland" },
  { abbr: "MA", name: "Massachusetts" },
  { abbr: "MI", name: "Michigan" },
  { abbr: "MN", name: "Minnesota" },
  { abbr: "MS", name: "Mississippi" },
  { abbr: "MO", name: "Missouri" },
  { abbr: "MT", name: "Montana" },
  { abbr: "NE", name: "Nebraska" },
  { abbr: "NV", name: "Nevada" },
  { abbr: "NH", name: "New Hampshire" },
  { abbr: "NJ", name: "New Jersey" },
  { abbr: "NM", name: "New Mexico" },
  { abbr: "NY", name: "New York" },
  { abbr: "NC", name: "North Carolina" },
  { abbr: "ND", name: "North Dakota" },
  { abbr: "OH", name: "Ohio" },
  { abbr: "OK", name: "Oklahoma" },
  { abbr: "OR", name: "Oregon" },
  { abbr: "PA", name: "Pennsylvania" },
  { abbr: "RI", name: "Rhode Island" },
  { abbr: "SC", name: "South Carolina" },
  { abbr: "SD", name: "South Dakota" },
  { abbr: "TN", name: "Tennessee" },
  { abbr: "TX", name: "Texas" },
  { abbr: "UT", name: "Utah" },
  { abbr: "VT", name: "Vermont" },
  { abbr: "VA", name: "Virginia" },
  { abbr: "WA", name: "Washington" },
  { abbr: "WV", name: "West Virginia" },
  { abbr: "WI", name: "Wisconsin" },
  { abbr: "WY", name: "Wyoming" },
];

function Quote() {
  const form = useRef();
  const [submitted, setSubmitted] = useState(false);
  const { lead, setLead } = useLead();

  function onChange(e) {
    const { name, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    // Map EmailJS field names → lead state keys
    const mapping = {
      user_first_name: "firstName",
      user_last_name: "lastName",
      user_email: "email",
      user_phone: "phone",
      user_dob: "dob",
      user_zip: "zip",
      user_state: "state",
      user_sex: "sex",
      user_tobacco: "tobacco",
      user_consent: "consent",
      message: "message",
    };
    const key = mapping[name] || name;
    setLead((prev) => ({ ...prev, [key]: value }));
  }

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        "service_76s388m",
        "template_4kwsimj",
        form.current,
        "qZfmJR1vYNBv-OJd1"
      )
      .then(
        () => {
          setSubmitted(true);
          form.current.reset();
          setLead({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            dob: "",
            zip: "",
            state: "",
            sex: "",
            tobacco: "",
            consent: false,
            message: "",
          });
        },
        (error) => {
          console.error("FAILED:", error.text);
        }
      );
  };

  return (
    <div className="quote-container">
      <section className="quote-page">
        <h1>Get a Free Insurance Quote</h1>
        <p>
          Fill out the information below and one of our agents will reach out
          with personalized options for you.
        </p>

        {submitted ? (
          <div className="thank-you">
            Thank you for requesting a quote. We'll contact you soon.
          </div>
        ) : (
          <form ref={form} onSubmit={sendEmail} className="quote-form">
            {/* First & Last Name */}
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="user_first_name"
              required
              value={lead.firstName || ""}
              onChange={onChange}
              autoComplete="given-name"
            />

            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="user_last_name"
              required
              value={lead.lastName || ""}
              onChange={onChange}
              autoComplete="family-name"
            />

            {/* Email */}
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="user_email"
              required
              value={lead.email || ""}
              onChange={onChange}
              autoComplete="email"
            />

            {/* Phone */}
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="user_phone"
              value={lead.phone || ""}
              onChange={onChange}
              autoComplete="tel"
            />

            {/* DOB */}
            <label htmlFor="dob">Date of Birth</label>
            <input
              type="date"
              id="dob"
              name="user_dob"
              required
              value={lead.dob || ""}
              onChange={onChange}
            />

            {/* Zip */}
            <label htmlFor="zip">Zip Code</label>
            <input
              type="text"
              id="zip"
              name="user_zip"
              required
              value={lead.zip || ""}
              onChange={onChange}
              inputMode="numeric"
              pattern="\d{5}"
              title="5-digit ZIP code"
              autoComplete="postal-code"
            />

            {/* State */}
            <label htmlFor="state">State</label>
            <select
              id="state"
              name="user_state"
              required
              value={lead.state || ""}
              onChange={onChange}
              autoComplete="address-level1"
            >
              {STATES.map((s) => (
                <option key={s.abbr} value={s.abbr}>
                  {s.name}
                </option>
              ))}
            </select>

            {/* Sex */}
            <label htmlFor="sex">Sex</label>
            <select
              id="sex"
              name="user_sex"
              required
              value={lead.sex || ""}
              onChange={onChange}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>

            {/* Tobacco */}
            <label htmlFor="tobacco">Tobacco Use</label>
            <select
              id="tobacco"
              name="user_tobacco"
              required
              value={lead.tobacco || ""}
              onChange={onChange}
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            {/* Notes */}
            <label htmlFor="message">Additional Notes</label>
            <textarea
              id="message"
              name="message"
              rows="4"
              value={lead.message || ""}
              onChange={onChange}
            ></textarea>

            {/* Consent */}
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="consent"
                name="user_consent"
                required
                checked={!!lead.consent}
                onChange={onChange}
              />
              <label htmlFor="consent">
                I agree to be contacted regarding my insurance inquiry.
              </label>
            </div>

            {/* Expandable Legal Consent */}
            <details className="consent-details">
              <summary>Read full consent agreement</summary>
              <p>
                By checking this box, I authorize MedaHealth Associates to
                contact me by phone, text message, or email regarding insurance
                products and services, including marketing communications. I
                understand that consent is not a condition of purchase, that
                message and data rates may apply, and that I may revoke my
                consent at any time.
              </p>
            </details>

            <button type="submit">Request Quote</button>
          </form>
        )}
      </section>
    </div>
  );
}

export default Quote;
